import { ObjectKeyPicker, ObjectKeyArrayPicker } from './utils/types'
import { cx, ClassValue } from './cx'
import { mergeProps } from './utils/merge-props'

export type ClassVariantRecord = Record<string, Record<string, ClassValue>>

export type ClassVariantExtendProps = { className: ClassValue }

export interface ClassVariantDefinition<T extends ClassVariantRecord | undefined> {
  base?: ClassValue
  variants?: T
  compoundVariants?: (ObjectKeyArrayPicker<T> & ClassVariantExtendProps)[]
  defaultVariants?: ObjectKeyPicker<T>
  classNameResolver?: typeof cx
}

export type ClassVariantFnProps<T extends ClassVariantRecord | undefined> = T extends undefined
  ? Partial<ClassVariantExtendProps>
  : ObjectKeyPicker<T> & Partial<ClassVariantExtendProps>

export type ClassVariantFn<T extends ClassVariantRecord | undefined> = (props?: ClassVariantFnProps<T>) => string

export type ClassVariantCreatorFn = <T extends ClassVariantRecord | undefined>(
  config: ClassVariantDefinition<T>
) => ClassVariantFn<T>

/**
 * Creates a class variant function that combines base classes, variants, compound variants, and default variants.
 *
 * @template T - Type of the variant record
 * @param config - Configuration object for creating class variants
 * @returns A function that accepts variant props and returns a combined class string
 *
 * @example
 * ```typescript
 * const button = cv({
 *   base: 'px-4 py-2 rounded',
 *   variants: {
 *     color: {
 *       primary: 'bg-blue-500 text-white',
 *       secondary: 'bg-gray-500 text-white'
 *     },
 *     size: {
 *       sm: 'text-sm',
 *       lg: 'text-lg'
 *     }
 *   },
 *   defaultVariants: {
 *     color: 'primary',
 *     size: 'sm'
 *   }
 * });
 *
 * button(); // => 'px-4 py-2 rounded bg-blue-500 text-white text-sm'
 * button({ color: 'secondary' }); // => 'px-4 py-2 rounded bg-gray-500 text-white text-sm'
 * ```
 */
export const cv: ClassVariantCreatorFn = (config) => {
  const { base, variants, compoundVariants, defaultVariants, classNameResolver = cx } = config

  if (!variants) {
    return (props) => classNameResolver(base, props?.className)
  }

  return (props) => {
    const mergedProps = mergeProps(defaultVariants, props, ['className'])

    const classValues: ClassValue[] = []

    for (const key in mergedProps) {
      const classValue = variants[key]?.[mergedProps[key] as string]
      if (classValue) {
        classValues.push(classValue)
      }
    }

    if (compoundVariants) {
      for (let i = 0; i < compoundVariants.length; i++) {
        const compound = compoundVariants[i]
        let matches = true
        for (const key in compound) {
          if (key === 'className') continue
          const value = compound[key as keyof typeof compound]
          const propValue = mergedProps[key]
          if (Array.isArray(value) ? !value.includes(propValue) : value !== propValue) {
            matches = false
            break
          }
        }
        if (matches && compound.className) {
          classValues.push(compound.className)
        }
      }
    }

    return classNameResolver(base, classValues, props?.className)
  }
}

export default cv
